import React, { useCallback, useState } from 'react'

import { LuChevronDown } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import { WikiSourceCitation } from '@/components/pursuits/wiki/ui/WikiSourceCitation'

/**
 * Collapsible section component (for Details and Diagrams)
 */

interface CollapsibleSectionProps {
  children?: React.ReactNode
  title: string
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  children,
  title,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const handleToggleOpen = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen, setIsOpen])

  if (!children) return null

  return (
    <div className="my-8">
      <button
        type="button"
        onClick={handleToggleOpen}
        className="group flex items-center justify-between w-full text-left py-2.5 px-3 rounded-lg bg-primary-100 dark:bg-primary-800/50 hover:bg-primary-100 dark:hover:bg-primary-800/70 border border-primary-200 dark:border-primary-800 transition-all duration-200"
      >
        <span className="text-base font-semibold text-primary-800 dark:text-primary-200">
          {title}
        </span>
        <LuChevronDown
          className={twMerge(
            'w-5 h-5 text-primary-400 dark:text-primary-500 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </button>
      <div
        className={twMerge(
          'grid transition-all duration-300 ease-out',
          isOpen
            ? 'grid-rows-[1fr] opacity-100 mt-4'
            : 'grid-rows-[0fr] opacity-0',
        )}
      >
        <div className="overflow-hidden">
          <div className="pl-4 border-l-2 w-full border-primary-500/30 dark:border-primary-400/30 prose prose-zinc america:prose-blue dark:prose-invert max-w-none">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Custom markdown component overrides for wiki content
 * Provides consistent styling that matches the app's design system
 */
export const markdownComponents = {
  // Citations - use the same component as Chat V2
  'source-citation': WikiSourceCitation,

  // Details sections
  detailssection: ({ children, title, ...props }: any) => (
    <CollapsibleSection title={title || 'Details'}>
      {children}
    </CollapsibleSection>
  ),

  // Diagram sections
  diagramsection: ({ children, title, ...props }: any) => (
    <CollapsibleSection title={title || 'View Diagram'}>
      {children}
    </CollapsibleSection>
  ),

  // Tables
  table: ({ className, ...props }: React.HTMLProps<HTMLTableElement>) => (
    <table
      className={twMerge(
        'overflow-x-auto border border-primary-200 dark:border-primary-800 w-full border-collapse',
        className,
      )}
      {...props}
    />
  ),
  thead: ({
    className,
    ...props
  }: React.HTMLProps<HTMLTableSectionElement>) => (
    <thead
      className={twMerge('bg-primary-100 dark:bg-primary-800/80', className)}
      {...props}
    />
  ),
  th: ({ className, ...props }: React.HTMLProps<HTMLTableCellElement>) => (
    <th
      className={twMerge(
        'bg-primary-100 dark:bg-primary-800/80',
        'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-300 border-b border-primary-200 dark:border-primary-800 [&[align=center]]:text-center [&[align=right]]:text-right',
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }: React.HTMLProps<HTMLTableCellElement>) => (
    <td
      className={twMerge(
        'px-4 py-3 text-sm text-primary-700 dark:text-primary-300 border-b border-primary-200 dark:border-primary-800 [&[align=center]]:text-center [&[align=right]]:text-right',
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }: React.HTMLProps<HTMLTableRowElement>) => (
    <tr
      className={twMerge(
        'bg-white dark:bg-primary-900/50 hover:bg-primary-50 dark:hover:bg-primary-800/50 transition-colors',
        className,
      )}
      {...props}
    />
  ),

  // Lists
  ul: ({ className, ...props }: React.HTMLProps<HTMLUListElement>) => (
    <ul
      className={twMerge('my-4 pl-0 space-y-2 list-none', className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }: React.HTMLProps<HTMLOListElement>) => {
    const { type, ...restProps } = props
    return (
      <ol
        className={twMerge(
          'my-4 pl-0 space-y-2 list-none counter-reset-[item]',
          className,
        )}
        {...restProps}
      />
    )
  },
  li: ({ className, children, ...props }: React.HTMLProps<HTMLLIElement>) => (
    <li
      className={twMerge(
        'relative pl-6 before:absolute before:left-0 before:top-2.5 before:w-1.5 before:h-1.5 before:rounded-full before:bg-accent-500 dark:before:bg-accent-400',
        className,
      )}
      {...props}
    >
      {children}
    </li>
  ),

  // Headings
  h1: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => {
    const text = typeof children === 'string' ? children : ''
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
    return (
      <h1
        id={id}
        className={twMerge(
          'text-4xl font-bold mt-12 mb-6 first:mt-0 text-primary-900 dark:text-primary-100 tracking-tight',
          className,
        )}
        {...props}
      >
        {children}
      </h1>
    )
  },
  h2: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => {
    const text = typeof children === 'string' ? children : ''
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
    return (
      <h2
        id={id}
        className={twMerge(
          'text-2xl font-bold mt-12 mb-4 pt-8 first:mt-0 first:pt-0 text-primary-900 dark:text-primary-100 tracking-tight border-t border-primary-200/80 dark:border-primary-700/50 first:border-t-0',
          className,
        )}
        {...props}
      >
        {children}
      </h2>
    )
  },
  h3: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => {
    const text = typeof children === 'string' ? children : ''
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
    return (
      <h3
        id={id}
        className={twMerge(
          'text-xl font-semibold mt-8 mb-3 text-primary-800 dark:text-primary-200 tracking-tight',
          className,
        )}
        {...props}
      >
        {children}
      </h3>
    )
  },
  h4: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => {
    const text = typeof children === 'string' ? children : ''
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
    return (
      <h4
        id={id}
        className={twMerge(
          'text-lg font-semibold mt-6 mb-2 text-primary-800 dark:text-primary-200',
          className,
        )}
        {...props}
      >
        {children}
      </h4>
    )
  },

  // Text elements
  p: ({ className, ...props }: React.HTMLProps<HTMLParagraphElement>) => (
    <p
      className={twMerge(
        'my-4 leading-7 text-primary-700 dark:text-primary-300',
        className,
      )}
      {...props}
    />
  ),
  strong: ({ className, ...props }: React.HTMLProps<HTMLElement>) => (
    <strong
      className={twMerge(
        'font-semibold text-primary-900 dark:text-primary-100',
        className,
      )}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }: React.HTMLProps<HTMLQuoteElement>) => (
    <blockquote
      className={twMerge(
        'my-6 pl-5 py-1 border-l-4 border-primary-500 dark:border-primary-400 bg-primary-50/50 dark:bg-primary-950/20 rounded-r-lg text-primary-700 dark:text-primary-300 italic [&>p]:my-2',
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, ...props }: React.HTMLProps<HTMLAnchorElement>) => (
    <a
      className={twMerge(
        'text-blue-600 dark:text-blue-400 hover:underline underline-offset-2',
        className,
      )}
      {...props}
    />
  ),
  code: ({ className, ...props }: React.HTMLProps<HTMLElement>) => (
    <code
      className={twMerge(
        'px-1.5 py-0.5 rounded-md bg-primary-100 dark:bg-primary-800 text-sm font-mono text-primary-800 dark:text-primary-200 border border-primary-200 dark:border-primary-700',
        className,
      )}
      {...props}
    />
  ),
  pre: ({ className, ...props }: React.HTMLProps<HTMLPreElement>) => (
    <pre
      className={twMerge(
        'my-6 p-4 rounded-xl bg-primary-900 dark:bg-primary-950 text-primary-100 overflow-x-auto text-sm font-mono border border-primary-800',
        className,
      )}
      {...props}
    />
  ),
  hr: ({ className, ...props }: React.HTMLProps<HTMLHRElement>) => (
    <hr
      className={twMerge(
        'my-8 border-0 h-px bg-gradient-to-r from-transparent via-primary-300 dark:via-primary-700 to-transparent',
        className,
      )}
      {...props}
    />
  ),
}
